import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

export type StagePipState = 'unknown' | 'not-started' | 'in-progress' | 'blocked' | 'ready' | 'attested';

export interface StageStripItem {
  id: string;
  label: string;
  pipState?: StagePipState;
  blockedCount?: number;
}

export interface StageStripProps {
  stages: StageStripItem[];
  activeStageId: string;
  frontierStageId?: string;
  onNavigate: (stageId: string) => void;
  className?: string;
}

const PIP_STYLES: Record<StagePipState, string> = {
  unknown: 'border border-[var(--color-border-sub)] bg-transparent',
  'not-started': 'border border-[var(--color-border-sub)] bg-transparent',
  'in-progress': 'bg-[var(--color-text-dim)]',
  ready: 'bg-[var(--color-text-primary)]',
  blocked: 'bg-[var(--color-blocker)]',
  attested: 'bg-[var(--color-verified)]',
};

function Pip({ state }: { state: StagePipState }) {
  return (
    <span
      className={cn(
        'relative flex items-center justify-center w-[10px] h-[10px] rounded-[2px]',
        PIP_STYLES[state]
      )}
    >
      {state === 'unknown' && (
        <svg width="4" height="1" viewBox="0 0 4 1" className="text-[var(--color-text-ghost)]">
          <line x1="0" y1="0.5" x2="4" y2="0.5" stroke="currentColor" strokeWidth="1" />
        </svg>
      )}
      {state === 'attested' && (
        <Check className="w-1.5 h-1.5 text-[var(--color-bg-base)]" strokeWidth={3} />
      )}
    </span>
  );
}

export function StageStrip({
  stages,
  activeStageId,
  frontierStageId,
  onNavigate,
  className,
}: StageStripProps) {
  const frontierStage = frontierStageId
    ? stages.find((s) => s.id === frontierStageId)
    : undefined;
  const isFrontierActive = activeStageId === frontierStageId;

  return (
    <div
      role="tablist"
      className={cn(
        'h-[64px] flex items-center overflow-x-auto bg-[var(--color-bg-base)] border-b border-[var(--color-border)]',
        className
      )}
    >
      <div className="flex items-center px-4 gap-[16px] min-w-max">
        {stages.map((stage, index) => {
          const isActive = stage.id === activeStageId;
          const isFrontier = stage.id === frontierStageId;
          const pipState = stage.pipState ?? 'unknown';

          return (
            <button
              key={stage.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onNavigate(stage.id)}
              className={cn(
                'relative flex items-center min-w-[112px] px-3 py-2 transition-colors duration-150',
                'hover:bg-[var(--color-bg-hover)]'
              )}
            >
              {isFrontier && (
                <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    className="text-[var(--color-text-primary)]"
                  >
                    <polygon points="0,0 8,0 4,8" fill="currentColor" />
                  </svg>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Pip state={pipState} />

                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                  {index + 1}
                </span>

                <span
                  className={cn(
                    'font-mono text-[10px] uppercase tracking-[0.08em]',
                    isActive
                      ? 'text-[var(--color-needs-human-text)]'
                      : 'text-[var(--color-text-secondary)]'
                  )}
                >
                  {stage.label}
                </span>

                {pipState === 'blocked' && stage.blockedCount && stage.blockedCount > 0 && (
                  <span className="font-mono text-[10px] text-[var(--color-blocker)]">
                    ▪{stage.blockedCount}
                  </span>
                )}
              </div>

              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-brand)]" />
              )}
            </button>
          );
        })}

        {!isFrontierActive && frontierStage && (
          <button
            onClick={() => onNavigate(frontierStage.id)}
            className="flex items-center gap-2 font-mono text-[11px] text-[var(--color-needs-human-text)] hover:opacity-80 transition-opacity duration-150 ml-4"
          >
            <span>⇢</span>
            <span>CASE FRONTIER: {frontierStage.label}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default StageStrip;
